// AGC: VoiceCommandsAdmin - Admin panel for voice command values configuration
// AGC: Organized by sector: Field, Financial, Task, Inventory
// AGC: Allows adding/editing/deleting voice command aliases and values

import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useTranslation } from '../../hooks/useTranslation'
import {
  Mic,
  Save,
  Plus,
  Trash2,
  Edit2,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Loader2,
  RefreshCw,
  Copy,
  Search,
} from 'lucide-react'
import { getFirestore, doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore'

// AGC: Type for a single voice command value (like "soja", "em-desenvolvimento", etc.)
interface VoiceCommandValue {
  id: string
  label: string
  aliases: string[]
  mapsTo?: string // AGC: Optional - maps to another value
  color?: string // AGC: Optional display color
  icon?: string // AGC: Optional icon
  description?: string
}

// AGC: Type for a category of values (like "cropType", "managementStatus")
interface VoiceCommandCategory {
  id: string
  label: string
  description: string
  values: VoiceCommandValue[]
}

// AGC: Type for a sector (like "field", "financial", "task")
interface VoiceCommandSector {
  id: string
  label: string
  icon: string
  color: string
  categories: VoiceCommandCategory[]
}

// AGC: Default configuration - this matches voiceCommands.js structure
const DEFAULT_SECTORS: VoiceCommandSector[] = [
  {
    id: 'field',
    label: 'Campo/Talhão',
    icon: '🌾',
    color: 'green',
    categories: [
      {
        id: 'managementStatus',
        label: 'Estado do Talhão',
        description: 'Estados de gerenciamento do campo (por-semear, semeado, em-desenvolvimento, pronto-colher)',
        values: [
          { id: 'por-semear', label: 'Por Semear', aliases: ['por semear', 'para semear', 'não semeado', 'pra semear'] },
          { id: 'semeado', label: 'Semeado', aliases: ['semeado', 'semeada', 'plantado', 'plantada'] },
          { id: 'em-desenvolvimento', label: 'Em Desenvolvimento', aliases: ['em desenvolvimento', 'desenvolvimento', 'crescendo', 'vegetativo'] },
          { id: 'pronto-colher', label: 'Pronto p/ Colher', aliases: ['pronto colher', 'pronto para colher', 'colheita', 'maduro', 'madura'] },
          { id: 'dessecacao', label: 'Dessecação', aliases: ['dessecação', 'dessecar', 'dissecação', 'dissecar'], mapsTo: 'pronto-colher' },
          { id: 'preparo', label: 'Preparo do Solo', aliases: ['preparo', 'preparando', 'aração', 'gradagem'], mapsTo: 'por-semear' },
          { id: 'florescendo', label: 'Florescendo', aliases: ['florescendo', 'floração', 'florindo'], mapsTo: 'em-desenvolvimento' },
          { id: 'pulverizacao', label: 'Pulverização', aliases: ['pulverização', 'pulverizar', 'a pulverizar', 'pulverizando', 'aplicação'], mapsTo: 'em-desenvolvimento' },
          { id: 'irrigacao', label: 'Irrigação', aliases: ['irrigação', 'irrigar', 'a irrigar', 'irrigando', 'regar'], mapsTo: 'em-desenvolvimento' },
          { id: 'adubacao', label: 'Adubação', aliases: ['adubação', 'adubar', 'a adubar', 'adubando', 'fertilização'], mapsTo: 'em-desenvolvimento' },
        ],
      },
      {
        id: 'cropType',
        label: 'Tipo de Cultivo',
        description: 'Culturas plantadas no campo (soja, milho, trigo, etc.)',
        values: [
          { id: 'soja', label: 'Soja', aliases: ['soja', 'soybean'] },
          { id: 'milho', label: 'Milho', aliases: ['milho', 'corn', 'maiz'] },
          { id: 'trigo', label: 'Trigo', aliases: ['trigo', 'wheat'] },
          { id: 'algodao', label: 'Algodão', aliases: ['algodão', 'algodao', 'cotton'] },
          { id: 'cafe', label: 'Café', aliases: ['café', 'cafe', 'coffee'] },
          { id: 'cana', label: 'Cana', aliases: ['cana', 'cana-de-açúcar', 'sugarcane'] },
          { id: 'feijao', label: 'Feijão', aliases: ['feijão', 'feijao', 'poroto', 'beans'] },
          { id: 'arroz', label: 'Arroz', aliases: ['arroz', 'rice'] },
          { id: 'outros', label: 'Outros', aliases: ['outros', 'outro', 'other'] },
        ],
      },
      {
        id: 'growthStage',
        label: 'Estágio de Crescimento',
        description: 'Fase de desenvolvimento da planta',
        values: [
          { id: 'plantio', label: 'Plantio', aliases: ['plantio', 'plantado', 'semeado'] },
          { id: 'germinacao', label: 'Germinação', aliases: ['germinação', 'germinando', 'germinacao'] },
          { id: 'vegetativo', label: 'Vegetativo', aliases: ['vegetativo', 'vegetativa', 'crescimento'] },
          { id: 'floracao', label: 'Floração', aliases: ['floração', 'floracao', 'florescendo', 'flores'] },
          { id: 'frutificacao', label: 'Frutificação', aliases: ['frutificação', 'frutificacao', 'frutos'] },
          { id: 'maturacao', label: 'Maturação', aliases: ['maturação', 'maturacao', 'amadurecendo', 'maduro'] },
          { id: 'colheita', label: 'Colheita', aliases: ['colheita', 'colhendo', 'colhido'] },
        ],
      },
      {
        id: 'healthStatus',
        label: 'Estado de Saúde',
        description: 'Condição de saúde da plantação',
        values: [
          { id: 'excelente', label: 'Excelente', aliases: ['excelente', 'ótimo', 'ótima', 'perfeito', 'perfeita'] },
          { id: 'bom', label: 'Bom', aliases: ['bom', 'boa', 'bem', 'ok', 'normal'] },
          { id: 'regular', label: 'Regular', aliases: ['regular', 'razoável', 'médio', 'média'] },
          { id: 'ruim', label: 'Ruim', aliases: ['ruim', 'mal', 'pobre', 'fraco', 'fraca'] },
          { id: 'critico', label: 'Crítico', aliases: ['crítico', 'critico', 'crítica', 'grave', 'sério', 'séria'] },
        ],
      },
      {
        id: 'soilType',
        label: 'Tipo de Solo',
        description: 'Classificação do solo do campo',
        values: [
          { id: 'argiloso', label: 'Argiloso', aliases: ['argiloso', 'argila', 'clay'] },
          { id: 'arenoso', label: 'Arenoso', aliases: ['arenoso', 'areia', 'sandy'] },
          { id: 'franco', label: 'Franco', aliases: ['franco', 'loam'] },
          { id: 'siltoso', label: 'Siltoso', aliases: ['siltoso', 'silte', 'silt'] },
          { id: 'humoso', label: 'Humoso', aliases: ['humoso', 'húmus', 'orgânico'] },
        ],
      },
    ],
  },
  {
    id: 'financial',
    label: 'Financeiro',
    icon: '💰',
    color: 'yellow',
    categories: [
      {
        id: 'expenseCategory',
        label: 'Categoria de Despesa',
        description: 'Tipos de despesas da fazenda',
        values: [
          { id: 'combustivel', label: 'Combustível', aliases: ['combustível', 'combustivel', 'diesel', 'gasolina', 'etanol', 'fuel'] },
          { id: 'sementes', label: 'Sementes', aliases: ['sementes', 'semente', 'seeds'] },
          { id: 'fertilizante', label: 'Fertilizante', aliases: ['fertilizante', 'adubo', 'npk', 'ureia', 'fertilizer'] },
          { id: 'defensivos', label: 'Defensivos', aliases: ['defensivos', 'agrotóxico', 'herbicida', 'fungicida', 'inseticida', 'pesticide'] },
          { id: 'mao-de-obra', label: 'Mão de Obra', aliases: ['mão de obra', 'mao de obra', 'salário', 'salario', 'labor'] },
          { id: 'manutencao', label: 'Manutenção', aliases: ['manutenção', 'manutencao', 'reparo', 'conserto', 'maintenance'] },
          { id: 'equipamento', label: 'Equipamento', aliases: ['equipamento', 'máquina', 'maquina', 'implemento', 'equipment'] },
          { id: 'aluguel', label: 'Aluguel', aliases: ['aluguel', 'arrendamento', 'rent'] },
          { id: 'outros', label: 'Outros', aliases: ['outros', 'outro', 'other'] },
        ],
      },
      {
        id: 'incomeCategory',
        label: 'Categoria de Receita',
        description: 'Tipos de receitas da fazenda (vendas de grãos, maquinário, ferramentas, agroquímicos, ativos, serviços, etc.)',
        values: [
          { id: 'venda-graos', label: 'Venda de Grãos', aliases: ['venda grãos', 'venda graos', 'vendeu grãos', 'grain sale', 'soja', 'milho', 'trigo', 'arroz', 'safra'] },
          { id: 'venda-maquinario', label: 'Venda de Maquinário', aliases: ['venda maquinário', 'venda maquinario', 'vendeu trator', 'vendeu colheitadeira', 'vendeu máquina', 'machinery sale'] },
          { id: 'venda-ferramentas', label: 'Venda de Ferramentas', aliases: ['venda ferramenta', 'venda ferramentas', 'vendeu ferramenta', 'tool sale', 'tools'] },
          { id: 'venda-agroquimico', label: 'Venda de Agroquímico', aliases: ['venda agroquímico', 'venda agroquimico', 'venda defensivo', 'venda herbicida', 'venda fungicida', 'vendeu defensivo', 'agrochemical sale'] },
          { id: 'venda-estoque', label: 'Venda de Estoque', aliases: ['venda estoque', 'venda de estoque', 'vendeu do estoque', 'stock sale', 'inventory sale'] },
          { id: 'venda-animais', label: 'Venda de Animais', aliases: ['venda animais', 'vendeu gado', 'vendeu boi', 'vendeu vaca', 'animal sale', 'cattle sale'] },
          { id: 'venda-ativos', label: 'Venda de Ativos', aliases: ['venda ativo', 'venda ativos', 'vendeu terreno', 'vendeu terra', 'vendeu propriedade', 'asset sale', 'land sale'] },
          { id: 'prestacao-servico', label: 'Prestação de Serviço', aliases: ['prestação serviço', 'serviço prestado', 'service', 'servicio'] },
          { id: 'subsidio', label: 'Subsídio', aliases: ['subsídio', 'subsidio', 'financiamento', 'crédito rural', 'incentivo', 'subsidy'] },
          { id: 'arrendamento', label: 'Arrendamento', aliases: ['arrendamento', 'aluguel recebido', 'lease income', 'rental income', 'alquiler'] },
          { id: 'outros', label: 'Outros', aliases: ['outros', 'outro', 'other', 'otro'] },
        ],
      },
    ],
  },
  {
    id: 'task',
    label: 'Tarefas',
    icon: '📋',
    color: 'blue',
    categories: [
      {
        id: 'taskCategory',
        label: 'Categoria de Tarefa',
        description: 'Tipos de tarefas agrícolas (pt/en/es)',
        values: [
          { id: 'irrigacao', label: 'Irrigação', aliases: ['irrigação', 'irrigacao', 'irrigar', 'regar', 'riego', 'irrigation', 'irrigate', 'water', 'watering'] },
          { id: 'pulverizacao', label: 'Pulverização', aliases: ['pulverização', 'pulverizacao', 'pulverizar', 'aplicar', 'aplicação', 'spraying', 'spray', 'fumigar', 'fumigacion', 'fumigação'] },
          { id: 'adubacao', label: 'Adubação', aliases: ['adubação', 'adubacao', 'adubar', 'fertilização', 'fertilizacao', 'fertilizar', 'abonar', 'fertilize', 'fertilization', 'fertilizing'] },
          { id: 'plantio', label: 'Plantio', aliases: ['plantio', 'plantar', 'semear', 'semeadura', 'planting', 'plant', 'sow', 'sowing', 'sembrar', 'siembra'] },
          { id: 'colheita', label: 'Colheita', aliases: ['colheita', 'colher', 'harvest', 'harvesting', 'cosecha', 'cosechar', 'recolher', 'recolha'] },
          { id: 'manutencao', label: 'Manutenção', aliases: ['manutenção', 'manutencao', 'manutenção preventiva', 'maintenance', 'mantenimiento', 'reparar', 'repair', 'conserto', 'reparo'] },
          { id: 'monitoramento', label: 'Monitoramento', aliases: ['monitoramento', 'monitorar', 'vistoriar', 'vistoria', 'monitoring', 'monitor', 'inspeccionar', 'inspecionar', 'inspeção', 'inspection'] },
          { id: 'preparo_solo', label: 'Preparo de Solo', aliases: ['preparo de solo', 'preparo solo', 'arar', 'gradear', 'gradagem', 'aração', 'soil preparation', 'preparacion de suelo', 'nivelamento'] },
          { id: 'dessecacao', label: 'Dessecação', aliases: ['dessecação', 'dessecacao', 'dessecar', 'desiccation', 'desecacion', 'burndown', 'dissecação', 'dissecar'] },
          { id: 'outros', label: 'Outros', aliases: ['outros', 'outro', 'other', 'otro', 'geral', 'general'] },
        ],
      },
      {
        id: 'taskPriority',
        label: 'Prioridade',
        description: 'Nível de prioridade da tarefa (pt/en/es)',
        values: [
          { id: 'alta', label: 'Alta', aliases: ['alta', 'urgente', 'urgentíssimo', 'urgentissimo', 'crítica', 'critica', 'high', 'urgent', 'critical', 'alta prioridad', 'prioritária', 'imediato'] },
          { id: 'media', label: 'Média', aliases: ['média', 'media', 'normal', 'moderada', 'medium', 'moderate', 'regular'] },
          { id: 'baixa', label: 'Baixa', aliases: ['baixa', 'low', 'baja', 'no rush', 'sem pressa', 'pode esperar', 'tranquilo'] },
        ],
      },
    ],
  },
  {
    id: 'inventory',
    label: 'Estoque',
    icon: '📦',
    color: 'purple',
    categories: [
      {
        id: 'inventoryCategory',
        label: 'Categoria de Estoque',
        description: 'Tipos de itens em estoque',
        values: [
          { id: 'sementes', label: 'Sementes', aliases: ['sementes', 'semente', 'seeds'] },
          { id: 'fertilizantes', label: 'Fertilizantes', aliases: ['fertilizantes', 'fertilizante', 'adubo', 'fertilizer'] },
          { id: 'defensivos', label: 'Defensivos', aliases: ['defensivos', 'defensivo', 'agrotóxico', 'pesticide'] },
          { id: 'combustivel', label: 'Combustível', aliases: ['combustível', 'combustivel', 'diesel', 'gasolina', 'fuel'] },
          { id: 'pecas', label: 'Peças', aliases: ['peças', 'pecas', 'peça', 'parts'] },
          { id: 'racao', label: 'Ração', aliases: ['ração', 'racao', 'feed'] },
          { id: 'outros', label: 'Outros', aliases: ['outros', 'outro', 'other'] },
        ],
      },
      {
        id: 'inventoryUnit',
        label: 'Unidade de Medida',
        description: 'Unidades para quantidades de estoque',
        values: [
          { id: 'kg', label: 'Quilogramas', aliases: ['kg', 'quilos', 'kilos', 'quilogramas'] },
          { id: 'litros', label: 'Litros', aliases: ['litros', 'litro', 'l', 'liters'] },
          { id: 'unidades', label: 'Unidades', aliases: ['unidades', 'unidade', 'un', 'units'] },
          { id: 'sacas', label: 'Sacas', aliases: ['sacas', 'saca', 'bags'] },
          { id: 'toneladas', label: 'Toneladas', aliases: ['toneladas', 'tonelada', 'ton', 'tons'] },
        ],
      },
    ],
  },
  {
    id: 'machinery',
    label: 'Maquinário',
    icon: '🚜',
    color: 'orange',
    categories: [
      {
        id: 'machineCategory',
        label: 'Categoria de Máquina',
        description: 'Tipos de máquinas agrícolas',
        values: [
          { id: 'trator', label: 'Trator', aliases: ['trator', 'tratores', 'tractor', 'tractors'] },
          { id: 'colheitadeira', label: 'Colheitadeira', aliases: ['colheitadeira', 'colhedeira', 'colheitadoras', 'combine', 'harvester'] },
          { id: 'pulverizador', label: 'Pulverizador', aliases: ['pulverizador', 'pulverizadores', 'sprayer', 'sprayers'] },
          { id: 'plantadeira', label: 'Plantadeira', aliases: ['plantadeira', 'plantadoras', 'semeadora', 'semeadeira', 'planter', 'seeder'] },
          { id: 'distribuidor', label: 'Distribuidor', aliases: ['distribuidor', 'adubadeira', 'adubadora', 'calcariadora', 'spreader'] },
          { id: 'implemento', label: 'Implemento', aliases: ['implemento', 'implementos', 'implement', 'grade', 'arado'] },
        ],
      },
      {
        id: 'tractorBrand',
        label: 'Marcas de Tratores',
        description: 'Fabricantes de tratores',
        values: [
          { id: 'john-deere', label: 'John Deere', aliases: ['john deere', 'johndere', 'jon dire', 'jondire', 'jd', 'deere'] },
          { id: 'case-ih', label: 'Case IH', aliases: ['case ih', 'case', 'caseih', 'case internacional'] },
          { id: 'new-holland', label: 'New Holland', aliases: ['new holland', 'newholland', 'niu holand', 'new holand', 'holland'] },
          { id: 'massey-ferguson', label: 'Massey Ferguson', aliases: ['massey ferguson', 'massey', 'massi ferguson', 'mf', 'massiferguson'] },
          { id: 'valtra', label: 'Valtra', aliases: ['valtra', 'valmet'] },
          { id: 'fendt', label: 'Fendt', aliases: ['fendt', 'fent'] },
          { id: 'kubota', label: 'Kubota', aliases: ['kubota', 'cubota'] },
          { id: 'mahindra', label: 'Mahindra', aliases: ['mahindra', 'maindra'] },
          { id: 'agrale', label: 'Agrale', aliases: ['agrale', 'agralle'] },
        ],
      },
      {
        id: 'harvesterBrand',
        label: 'Marcas de Colheitadeiras',
        description: 'Fabricantes de colheitadeiras',
        values: [
          { id: 'john-deere', label: 'John Deere', aliases: ['john deere', 'johndere', 'jon dire', 'jondire', 'jd', 'deere'] },
          { id: 'case-ih', label: 'Case IH', aliases: ['case ih', 'case', 'caseih', 'case internacional'] },
          { id: 'new-holland', label: 'New Holland', aliases: ['new holland', 'newholland', 'niu holand', 'new holand', 'holland'] },
          { id: 'massey-ferguson', label: 'Massey Ferguson', aliases: ['massey ferguson', 'massey', 'massi ferguson', 'mf', 'massiferguson'] },
          { id: 'claas', label: 'CLAAS', aliases: ['claas', 'clas', 'class'] },
          { id: 'ideal', label: 'Ideal', aliases: ['ideal'] },
        ],
      },
      {
        id: 'sprayerBrand',
        label: 'Marcas de Pulverizadores',
        description: 'Fabricantes de pulverizadores',
        values: [
          { id: 'john-deere', label: 'John Deere', aliases: ['john deere', 'johndere', 'jondire', 'jd', 'deere'] },
          { id: 'case-ih', label: 'Case IH', aliases: ['case ih', 'case', 'caseih'] },
          { id: 'new-holland', label: 'New Holland', aliases: ['new holland', 'newholland', 'holland'] },
          { id: 'massey-ferguson', label: 'Massey Ferguson', aliases: ['massey ferguson', 'massey', 'mf'] },
          { id: 'stara', label: 'Stara', aliases: ['stara', 'uniport'] },
          { id: 'jacto', label: 'Jacto', aliases: ['jacto', 'jacto uniport'] },
          { id: 'montana', label: 'Montana', aliases: ['montana'] },
          { id: 'pla', label: 'PLA', aliases: ['pla', 'p.l.a.'] },
        ],
      },
      {
        id: 'planterBrand',
        label: 'Marcas de Plantadeiras',
        description: 'Fabricantes de plantadeiras/semeadoras',
        values: [
          { id: 'john-deere', label: 'John Deere', aliases: ['john deere', 'johndere', 'jondire', 'jd', 'deere'] },
          { id: 'case-ih', label: 'Case IH', aliases: ['case ih', 'case', 'caseih'] },
          { id: 'new-holland', label: 'New Holland', aliases: ['new holland', 'newholland', 'holland'] },
          { id: 'massey-ferguson', label: 'Massey Ferguson', aliases: ['massey ferguson', 'massey', 'mf'] },
          { id: 'stara', label: 'Stara', aliases: ['stara'] },
          { id: 'tatu-marchesan', label: 'Tatu Marchesan', aliases: ['tatu marchesan', 'tatu', 'marchesan'] },
          { id: 'jumil', label: 'Jumil', aliases: ['jumil', 'jumiu'] },
          { id: 'baldan', label: 'Baldan', aliases: ['baldan'] },
          { id: 'semeato', label: 'Semeato', aliases: ['semeato'] },
          { id: 'vence-tudo', label: 'Vence Tudo', aliases: ['vence tudo', 'vencetudo'] },
          { id: 'kuhn', label: 'Kuhn', aliases: ['kuhn', 'kun'] },
          { id: 'piccin', label: 'Piccin', aliases: ['piccin', 'picin'] },
          { id: 'imasa', label: 'Imasa', aliases: ['imasa'] },
          { id: 'civemasa', label: 'Civemasa', aliases: ['civemasa'] },
          { id: 'kf', label: 'KF Implementos', aliases: ['kf', 'kf implementos'] },
        ],
      },
      {
        id: 'spreaderBrand',
        label: 'Marcas de Distribuidores',
        description: 'Fabricantes de distribuidores/adubadeiras',
        values: [
          { id: 'john-deere', label: 'John Deere', aliases: ['john deere', 'johndere', 'jd', 'deere'] },
          { id: 'case-ih', label: 'Case IH', aliases: ['case ih', 'case', 'caseih'] },
          { id: 'new-holland', label: 'New Holland', aliases: ['new holland', 'newholland', 'holland'] },
          { id: 'massey-ferguson', label: 'Massey Ferguson', aliases: ['massey ferguson', 'massey', 'mf'] },
          { id: 'stara', label: 'Stara', aliases: ['stara'] },
          { id: 'jacto', label: 'Jacto', aliases: ['jacto'] },
          { id: 'baldan', label: 'Baldan', aliases: ['baldan'] },
          { id: 'tatu-marchesan', label: 'Tatu Marchesan', aliases: ['tatu marchesan', 'tatu', 'marchesan'] },
          { id: 'jumil', label: 'Jumil', aliases: ['jumil'] },
          { id: 'semeato', label: 'Semeato', aliases: ['semeato'] },
          { id: 'vence-tudo', label: 'Vence Tudo', aliases: ['vence tudo', 'vencetudo'] },
          { id: 'piccin', label: 'Piccin', aliases: ['piccin', 'picin'] },
          { id: 'kuhn', label: 'Kuhn', aliases: ['kuhn', 'kun'] },
        ],
      },
    ],
  },
  {
    id: 'storage',
    label: 'Armazenamento',
    icon: '🛢️',
    color: 'orange',
    categories: [
      {
        id: 'fuelType',
        label: 'Tipos de Combustível',
        description: 'Tipos de combustível reconhecidos pelo sistema de voz para direcionamento a tanques',
        values: [
          { id: 'diesel', label: 'Diesel', aliases: ['diesel', 'diesel s10', 'diesel s500', 'oleo diesel', 'óleo diesel'] },
          { id: 'gasolina', label: 'Gasolina', aliases: ['gasolina', 'gasolina comum', 'gasolina aditivada'] },
          { id: 'etanol', label: 'Etanol', aliases: ['etanol', 'ethanol', 'álcool', 'alcool'] },
          { id: 'arla', label: 'Arla 32', aliases: ['arla', 'arla 32', 'arla32', 'uréia automotiva'] },
          { id: 'querosene', label: 'Querosene', aliases: ['querosene', 'querosine'] },
          { id: 'biodiesel', label: 'Biodiesel', aliases: ['biodiesel', 'bio diesel'] },
        ],
      },
      {
        id: 'tankOperation',
        label: 'Operações de Tanque',
        description: 'Comandos de voz para operações com tanques de combustível',
        values: [
          { id: 'encher', label: 'Abastecer', aliases: ['encher', 'abastecer', 'encher tanque', 'abastecer tanque', 'fill', 'fill tank', 'reabastecer', 'completar'] },
          { id: 'retirar', label: 'Retirar', aliases: ['retirar', 'tirar', 'retirar do tanque', 'esvaziar', 'consumir', 'drain', 'usar combustível'] },
          { id: 'nivel', label: 'Nível', aliases: ['nível', 'nivel', 'quanto tem', 'verificar tanque', 'checar tanque', 'check tank', 'volume'] },
          { id: 'transferir', label: 'Transferir', aliases: ['transferir', 'mover', 'passar', 'transfer', 'transferir combustível', 'passar combustível'] },
        ],
      },
      {
        id: 'siloOperation',
        label: 'Operações de Silo',
        description: 'Comandos de voz para operações com silos de grãos',
        values: [
          { id: 'armazenar', label: 'Armazenar', aliases: ['armazenar', 'guardar', 'estocar', 'ensacar', 'store', 'armazenar grão', 'guardar grão'] },
          { id: 'retirar_silo', label: 'Retirar', aliases: ['retirar do silo', 'tirar do silo', 'descarregar', 'expedir', 'remove'] },
          { id: 'nivel_silo', label: 'Nível', aliases: ['nível do silo', 'nivel do silo', 'quanto tem no silo', 'capacidade', 'estoque no silo'] },
        ],
      },
    ],
  },
  // ==========================================================
  // AGC: INTENTS sector — verbs that classify what the user wants to DO
  // AGC: Unlike other sectors (which hold values/brands/categories), this holds action verbs.
  // AGC: When a user adds a new verb here, the backend dynamically extends its regex patterns.
  // AGC: For example, adding "incorporar" to vehicleCreateVerbs makes "incorporar trator X" route to vehicle creation.
  // ==========================================================
  {
    id: 'intents',
    label: 'Comandos (Verbos)',
    icon: '🎯',
    color: 'purple',
    categories: [
      {
        id: 'vehicleCreateVerbs',
        label: 'Verbos — Criar Veículo/Máquina',
        description: 'Verbos que fazem o sistema entender "criar um trator/colheitadeira/máquina". Adicione sinônimos regionais se necessário.',
        values: [
          { id: 'cadastrar', label: 'Cadastrar', aliases: ['cadastrar', 'cadastrando'] },
          { id: 'registrar', label: 'Registrar', aliases: ['registrar', 'registrando'] },
          { id: 'adicionar', label: 'Adicionar', aliases: ['adicionar', 'adicionando'] },
          { id: 'criar', label: 'Criar', aliases: ['criar', 'criando', 'crie'] },
          { id: 'incluir', label: 'Incluir', aliases: ['incluir', 'incluindo'] },
          { id: 'inserir', label: 'Inserir', aliases: ['inserir', 'inserindo'] },
          { id: 'abrir', label: 'Abrir', aliases: ['abrir', 'abrindo'] },
          { id: 'colocar', label: 'Colocar', aliases: ['colocar', 'colocando'] },
        ],
      },
      {
        id: 'fieldCreateVerbs',
        label: 'Verbos — Criar Campo/Talhão',
        description: 'Verbos que fazem o sistema entender "criar um campo/talhão/lote/parcela". Adicione sinônimos regionais se necessário.',
        values: [
          { id: 'criar', label: 'Criar', aliases: ['criar', 'criando'] },
          { id: 'cadastrar', label: 'Cadastrar', aliases: ['cadastrar', 'cadastrando'] },
          { id: 'registrar', label: 'Registrar', aliases: ['registrar', 'registrando'] },
          { id: 'adicionar', label: 'Adicionar', aliases: ['adicionar', 'adicionando'] },
          { id: 'incluir', label: 'Incluir', aliases: ['incluir', 'incluindo'] },
          { id: 'inserir', label: 'Inserir', aliases: ['inserir', 'inserindo'] },
          { id: 'abrir', label: 'Abrir', aliases: ['abrir', 'abrindo'] },
        ],
      },
    ],
  },
]

/**
 * AGC: VoiceCommandsAdmin - Main component for voice command configuration
 */
export const VoiceCommandsAdmin: React.FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  
  // AGC: State for sectors data
  const [sectors, setSectors] = useState<VoiceCommandSector[]>(DEFAULT_SECTORS)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  
  // AGC: UI state
  const [activeSector, setActiveSector] = useState<string>('field')
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['managementStatus'])
  const [searchTerm, setSearchTerm] = useState('')
  
  // AGC: Edit state
  const [editingValue, setEditingValue] = useState<{
    sectorId: string
    categoryId: string
    valueId: string | null // AGC: null for new value
  } | null>(null)
  const [editForm, setEditForm] = useState<{
    id: string
    label: string
    aliases: string
    mapsTo: string
    description: string
  }>({ id: '', label: '', aliases: '', mapsTo: '', description: '' })

  // AGC: Load data from Firebase
  const loadData = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      const db = getFirestore()
      const docRef = doc(db, 'voiceConfig', 'commandValues')
      const docSnap = await getDoc(docRef)
      
      if (docSnap.exists()) {
        const data = docSnap.data()
        if (data.sectors) {
          setSectors(data.sectors)
          console.log('✅ AGC: Loaded voice command config from Firebase')
        }
      } else {
        // AGC: Initialize with defaults
        console.log('📝 AGC: No config found, using defaults')
        setSectors(DEFAULT_SECTORS)
      }
    } catch (err) {
      console.error('❌ AGC: Error loading voice config:', err)
      setError('Erro ao carregar configurações de voz')
    } finally {
      setIsLoading(false)
    }
  }, [])

  // AGC: Save data to Firebase
  const saveData = useCallback(async () => {
    setIsSaving(true)
    setError(null)
    
    try {
      const db = getFirestore()
      const docRef = doc(db, 'voiceConfig', 'commandValues')
      
      await setDoc(docRef, {
        sectors,
        updatedAt: new Date().toISOString(),
        updatedBy: user?.email || 'unknown',
      })
      
      setSuccessMessage('Configurações salvas com sucesso!')
      setTimeout(() => setSuccessMessage(null), 3000)
      console.log('✅ AGC: Voice command config saved to Firebase')
    } catch (err) {
      console.error('❌ AGC: Error saving voice config:', err)
      setError('Erro ao salvar configurações')
    } finally {
      setIsSaving(false)
    }
  }, [sectors, user])

  // AGC: Load on mount
  useEffect(() => {
    loadData()
  }, [loadData])

  // AGC: Toggle category expansion
  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    )
  }

  // AGC: Start editing a value
  const startEdit = (sectorId: string, categoryId: string, value: VoiceCommandValue | null) => {
    setEditingValue({
      sectorId,
      categoryId,
      valueId: value?.id || null,
    })
    setEditForm({
      id: value?.id || '',
      label: value?.label || '',
      aliases: value?.aliases.join(', ') || '',
      mapsTo: value?.mapsTo || '',
      description: value?.description || '',
    })
  }

  // AGC: Cancel editing
  const cancelEdit = () => {
    setEditingValue(null)
    setEditForm({ id: '', label: '', aliases: '', mapsTo: '', description: '' })
  }

  // AGC: Save edited value
  const saveEdit = () => {
    if (!editingValue) return
    if (!editForm.id || !editForm.label) {
      setError('ID e Label são obrigatórios')
      return
    }

    setSectors(prev => prev.map(sector => {
      if (sector.id !== editingValue.sectorId) return sector
      
      return {
        ...sector,
        categories: sector.categories.map(category => {
          if (category.id !== editingValue.categoryId) return category
          
          const newValue: VoiceCommandValue = {
            id: editForm.id.toLowerCase().replace(/\s+/g, '-'),
            label: editForm.label,
            aliases: editForm.aliases.split(',').map(a => a.trim().toLowerCase()).filter(Boolean),
            ...(editForm.mapsTo && { mapsTo: editForm.mapsTo }),
            ...(editForm.description && { description: editForm.description }),
          }
          
          if (editingValue.valueId) {
            // AGC: Editing existing value
            return {
              ...category,
              values: category.values.map(v =>
                v.id === editingValue.valueId ? newValue : v
              ),
            }
          } else {
            // AGC: Adding new value
            return {
              ...category,
              values: [...category.values, newValue],
            }
          }
        }),
      }
    }))
    
    cancelEdit()
  }

  // AGC: Delete value
  const deleteValue = (sectorId: string, categoryId: string, valueId: string) => {
    if (!confirm('Tem certeza que deseja excluir este valor?')) return
    
    setSectors(prev => prev.map(sector => {
      if (sector.id !== sectorId) return sector
      
      return {
        ...sector,
        categories: sector.categories.map(category => {
          if (category.id !== categoryId) return category
          
          return {
            ...category,
            values: category.values.filter(v => v.id !== valueId),
          }
        }),
      }
    }))
  }

  // AGC: Get active sector data
  const activeSectorData = sectors.find(s => s.id === activeSector)

  // AGC: Filter values by search term
  const filterValues = (values: VoiceCommandValue[]) => {
    if (!searchTerm) return values
    const term = searchTerm.toLowerCase()
    return values.filter(v =>
      v.id.includes(term) ||
      v.label.toLowerCase().includes(term) ||
      v.aliases.some(a => a.includes(term))
    )
  }

  // AGC: Get color classes for sector
  const getSectorColors = (color: string, isActive: boolean) => {
    const colors: Record<string, { active: string; inactive: string }> = {
      green: { active: 'bg-green-500 text-white', inactive: 'bg-green-100 text-green-700 hover:bg-green-200' },
      yellow: { active: 'bg-yellow-500 text-white', inactive: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' },
      blue: { active: 'bg-blue-500 text-white', inactive: 'bg-blue-100 text-blue-700 hover:bg-blue-200' },
      purple: { active: 'bg-purple-500 text-white', inactive: 'bg-purple-100 text-purple-700 hover:bg-purple-200' },
      orange: { active: 'bg-orange-500 text-white', inactive: 'bg-orange-100 text-orange-700 hover:bg-orange-200' },
    }
    return isActive ? colors[color]?.active : colors[color]?.inactive
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-green-500" />
        <span className="ml-3 text-slate-600 dark:text-slate-300">Carregando configurações...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* AGC: Header */}
      <div className="bg-white dark:bg-admin-card-dark rounded-lg shadow-card dark:shadow-none p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mic className="w-8 h-8 text-green-500" />
            <div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                Configuração de Comandos de Voz
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Configure valores e aliases para comandos de voz por setor
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={loadData}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Recarregar
            </button>

            <button
              onClick={saveData}
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-2 bg-brand-primary text-white dark:bg-brand-light dark:text-[#052e22] rounded-lg hover:bg-brand-dark dark:hover:opacity-90 disabled:opacity-50 transition-colors"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Salvar Alterações
            </button>
          </div>
        </div>

        {/* AGC: Messages */}
        {error && (
          <div className="mt-4 p-3 bg-error-50 dark:bg-red-500/15 text-error-700 dark:text-red-400 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {successMessage && (
          <div className="mt-4 p-3 bg-success-50 dark:bg-emerald-500/15 text-brand-primary dark:text-emerald-400 rounded-lg flex items-center gap-2">
            <Check className="w-5 h-5" />
            {successMessage}
          </div>
        )}
      </div>

      {/* AGC: Sector Tabs */}
      <div className="flex gap-2 flex-wrap">
        {sectors.map(sector => (
          <button
            key={sector.id}
            onClick={() => setActiveSector(sector.id)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${getSectorColors(
              sector.color,
              activeSector === sector.id
            )}`}
          >
            <span className="mr-2">{sector.icon}</span>
            {sector.label}
          </button>
        ))}
      </div>

      {/* AGC: Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 dark:text-slate-500" />
        <input
          type="text"
          placeholder="Buscar valores ou aliases..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-admin-bg-dark dark:text-slate-100"
        />
      </div>

      {/* AGC: Help banner for Intents sector — explains what verbs are and how to use them */}
      {activeSector === 'intents' && (
        <div className="bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/30 rounded-lg p-4 flex items-start gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-500/20 flex items-center justify-center">
            <span className="text-purple-700 dark:text-purple-300 font-bold text-lg">?</span>
          </div>
          <div className="flex-1 text-sm text-purple-900 dark:text-purple-300">
            <p className="font-semibold mb-2">O que são verbos de comando?</p>
            <p className="mb-2">
              Verbos são palavras que o sistema usa para entender <strong>o que você quer fazer</strong>.
              Por exemplo, "cadastrar", "criar", "adicionar" todos significam "quero criar algo novo".
            </p>
            <p className="mb-2">
              <strong>Como usar:</strong> Adicione sinônimos regionais ou palavras alternativas que os usuários da sua
              região costumam usar. O sistema reconhecerá automaticamente frases como
              <em> "[novo verbo] trator John Deere" </em> ou <em> "[novo verbo] talhão Norte"</em>.
            </p>
            <p className="mb-2">
              <strong>Exemplos:</strong>
              <br />
              • <code className="bg-purple-100 px-1 rounded">cadastrar</code> + <code className="bg-purple-100 px-1 rounded">trator</code> → "Cadastrar trator John Deere" cria um veículo
              <br />
              • <code className="bg-purple-100 px-1 rounded">criar</code> + <code className="bg-purple-100 px-1 rounded">talhão</code> → "Criar talhão Norte 25 hectares" cria um campo
            </p>
            <p className="text-xs text-purple-700">
              <strong>⚠️ Atenção:</strong> Adicionar verbos incorretos pode confundir o sistema (ex: "pulverizar" é ação de tarefa, não de criação).
              Use apenas verbos que signifiquem "criar", "registrar" ou "adicionar".
            </p>
          </div>
        </div>
      )}

      {/* AGC: Categories */}
      {activeSectorData && (
        <div className="space-y-4">
          {activeSectorData.categories.map(category => (
            <div key={category.id} className="bg-white dark:bg-admin-card-dark rounded-lg shadow-card dark:shadow-none overflow-hidden">
              {/* AGC: Category header */}
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full px-6 py-4 flex items-center justify-between bg-slate-50 dark:bg-white/[0.02] hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {expandedCategories.includes(category.id) ? (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  )}
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900 dark:text-slate-100">{category.label}</h3>
                      {activeSector === 'intents' && (
                        <span
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-xs font-bold cursor-help"
                          title={`Verbos que o sistema reconhece para ${category.label.toLowerCase()}. Clique para expandir e gerenciar os verbos.`}
                        >
                          ?
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{category.description}</p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-slate-200 dark:bg-white/10 text-slate-600 dark:text-slate-300 rounded-full text-sm">
                  {category.values.length} {activeSector === 'intents' ? 'verbos' : 'valores'}
                </span>
              </button>

              {/* AGC: Category content */}
              {expandedCategories.includes(category.id) && (
                <div className="p-6">
                  {/* AGC: Add new button */}
                  <button
                    onClick={() => startEdit(activeSectorData.id, category.id, null)}
                    className="mb-4 flex items-center gap-2 px-4 py-2 bg-success-50 dark:bg-emerald-500/15 text-brand-primary dark:text-emerald-400 rounded-lg hover:bg-green-100 dark:hover:bg-emerald-500/25 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Adicionar Novo Valor
                  </button>

                  {/* AGC: Values table */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 dark:bg-white/[0.02]">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-slate-300">ID</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-slate-300">Label</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-slate-300">Aliases</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-slate-300">Mapeia Para</th>
                          <th className="px-4 py-3 text-right text-sm font-medium text-slate-600 dark:text-slate-300">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 dark:divide-white/5">
                        {filterValues(category.values).map(value => (
                          <tr key={value.id} className="hover:bg-slate-50/50 dark:hover:bg-white/[0.02]">
                            <td className="px-4 py-3">
                              <code className="px-2 py-1 bg-slate-100 dark:bg-white/[0.04] rounded text-sm text-slate-700 dark:text-slate-300">{value.id}</code>
                            </td>
                            <td className="px-4 py-3 font-medium">{value.label}</td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {value.aliases.slice(0, 4).map((alias, i) => (
                                  <span
                                    key={i}
                                    className="px-2 py-0.5 bg-info-50 dark:bg-blue-500/15 text-info-700 dark:text-blue-400 rounded text-xs"
                                  >
                                    {alias}
                                  </span>
                                ))}
                                {value.aliases.length > 4 && (
                                  <span className="px-2 py-0.5 bg-slate-100 dark:bg-white/[0.04] text-slate-500 dark:text-slate-400 rounded text-xs">
                                    +{value.aliases.length - 4}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {value.mapsTo ? (
                                <span className="px-2 py-1 bg-warning-50 dark:bg-yellow-500/15 text-warning-700 dark:text-yellow-400 rounded text-sm">
                                  → {value.mapsTo}
                                </span>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => startEdit(activeSectorData.id, category.id, value)}
                                  className="p-2 text-blue-600 dark:text-blue-400 hover:bg-info-50 dark:hover:bg-blue-500/15 rounded transition-colors"
                                  title="Editar"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => deleteValue(activeSectorData.id, category.id, value.id)}
                                  className="p-2 text-error-700 dark:text-red-400 hover:bg-error-50 dark:hover:bg-red-500/15 rounded transition-colors"
                                  title="Excluir"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* AGC: Edit Modal */}
      {editingValue && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[90] p-4">
          <div className="bg-white dark:bg-admin-card-dark dark:border dark:border-white/5 rounded-lg shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-white/5 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {editingValue.valueId ? 'Editar Valor' : 'Novo Valor'}
              </h3>
              <button onClick={cancelEdit} className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  ID (identificador único)
                </label>
                <input
                  type="text"
                  value={editForm.id}
                  onChange={(e) => setEditForm({ ...editForm, id: e.target.value })}
                  placeholder="ex: em-desenvolvimento"
                  disabled={!!editingValue.valueId}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-admin-bg-dark dark:text-slate-100 disabled:bg-slate-100 dark:disabled:bg-white/[0.04]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Label (nome exibido)
                </label>
                <input
                  type="text"
                  value={editForm.label}
                  onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                  placeholder="ex: Em Desenvolvimento"
                  className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-admin-bg-dark dark:text-slate-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Aliases (separados por vírgula)
                </label>
                <textarea
                  value={editForm.aliases}
                  onChange={(e) => setEditForm({ ...editForm, aliases: e.target.value })}
                  placeholder="ex: desenvolvimento, crescendo, vegetativo"
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-admin-bg-dark dark:text-slate-100"
                />
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Palavras que o usuário pode falar para selecionar este valor
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Mapeia Para (opcional)
                </label>
                <input
                  type="text"
                  value={editForm.mapsTo}
                  onChange={(e) => setEditForm({ ...editForm, mapsTo: e.target.value })}
                  placeholder="ex: em-desenvolvimento"
                  className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-admin-bg-dark dark:text-slate-100"
                />
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Se preenchido, este valor será convertido para outro antes de salvar
                </p>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 dark:border-white/5 flex items-center justify-end gap-3">
              <button
                onClick={cancelEdit}
                className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={saveEdit}
                className="flex items-center gap-2 px-4 py-2 bg-brand-primary text-white dark:bg-brand-light dark:text-[#052e22] rounded-lg hover:bg-brand-dark dark:hover:opacity-90 transition-colors"
              >
                <Check className="w-4 h-4" />
                {editingValue.valueId ? 'Salvar' : 'Adicionar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AGC: Help section */}
      <div className="bg-info-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-info-700 dark:text-blue-400 mb-2">💡 Como usar</h3>
        <ul className="space-y-2 text-sm text-info-700 dark:text-blue-400">
          <li>• <strong>ID:</strong> Identificador único (usado internamente). Use letras minúsculas e hífens.</li>
          <li>• <strong>Label:</strong> Nome exibido na interface para o usuário.</li>
          <li>• <strong>Aliases:</strong> Palavras que o usuário pode falar para acionar este valor. Quanto mais aliases, melhor o reconhecimento.</li>
          <li>• <strong>Mapeia Para:</strong> Converte este valor para outro. Ex: "pulverização" mapeia para "em-desenvolvimento".</li>
          <li>• Clique em <strong>Salvar Alterações</strong> para persistir as mudanças no Firebase.</li>
          <li>• Reinicie o backend após salvar para aplicar as novas configurações.</li>
        </ul>
      </div>
    </div>
  )
}

export default VoiceCommandsAdmin





